<template>
    <div class="uploadLinks">
        <Box
            title="Páginas de Upload"
            sub-title="Gerencie páginas para que terceiros possam enviar arquivos para você"
            icon="upload"
            :variant="BOX_VARIANTS.DEFAULT"
            :actions="headerActions"
            @action-click="handleHeaderAction"
        >

            <!-- Cards de estatísticas -->
            <div class="upload_stats_section">
                <div class="row">
                    <!-- Total de Links -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="upload_stat_card upload_stat_card--primary">
                            <div class="upload_stat_header">
                                <div class="upload_stat_icon">
                                    <sw-icon name="upload" />
                                </div>
                                <div class="upload_stat_info">
                                    <span class="upload_stat_label">Total de Páginas</span>
                                    <div class="upload_stat_numbers">
                                        <span class="upload_stat_main">{{ totalUploads }}</span>
                                        <span class="upload_stat_secondary">páginas criadas</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Links Ativos -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="upload_stat_card upload_stat_card--success">
                            <div class="upload_stat_header">
                                <div class="upload_stat_icon">
                                    <sw-icon name="checkmark" />
                                </div>
                                <div class="upload_stat_info">
                                    <span class="upload_stat_label">Páginas Ativas</span>
                                    <div class="upload_stat_numbers">
                                        <span class="upload_stat_main">{{ activeUploadsCount }}</span>
                                        <span class="upload_stat_secondary">de {{ totalUploads }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Uploads Recebidos -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="upload_stat_card upload_stat_card--compact">
                            <div class="upload_stat_header">
                                <div class="upload_stat_icon">
                                    <sw-icon name="cloud" />
                                </div>
                                <div class="upload_stat_info">
                                    <span class="upload_stat_label">Uploads Recebidos</span>
                                    <div class="upload_stat_numbers">
                                        <span class="upload_stat_main">{{ totalReceived }}</span>
                                        <span class="upload_stat_secondary">este mês</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Pendentes -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="upload_stat_card upload_stat_card--compact">
                            <div class="upload_stat_header">
                                <div class="upload_stat_icon">
                                    <sw-icon name="clock" />
                                </div>
                                <div class="upload_stat_info">
                                    <span class="upload_stat_label">Pendentes</span>
                                    <div class="upload_stat_numbers">
                                        <span class="upload_stat_main">{{ totalPending }}</span>
                                        <span class="upload_stat_secondary">aguardando</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Seção de Filtros -->
            <FilterSection
                label="Filtrar por:"
                search-placeholder="Buscar páginas de upload..."
                :search-value="searchQuery"
                :filters="filterOptions"
                :filter-values="filterValues"
                @update:search="searchQuery = $event"
                @update:filter="updateFilter"
                @clear-filters="clearFilters"
            />

        </Box>

        <!-- Loading State -->
        <div class="upload_loading_section" v-if="loadingUploads">
            <app-spinner text="Carregando páginas de upload..." />
        </div>

        <!-- Lista de uploads -->
        <div class="upload_content" v-else-if="filteredUploads.length > 0">
            <div class="row">
                <div
                    v-for="upload in filteredUploads"
                    :key="upload.id"
                    class="grid_col_12"
                >
                    <UploadLinkCard
                        :item="upload"
                        @view="viewUpload"
                        @edit="editUpload"
                        @delete="deleteUpload"
                        @copy="handleCopyLink"
                    />
                </div>
            </div>
        </div>

        <!-- Estado vazio -->
        <div class="upload_empty_section" v-else-if="uploads.length === 0">
            <EmptyState
                icon="upload"
                title="Nenhuma página criada"
                description="Crie sua primeira página de upload para permitir que terceiros enviem arquivos"
            >
                <template #actions>
                    <sw-button
                        name="Criar Primeira Página"
                        color="blue"
                        @click="addNewUpload"
                    />
                </template>
            </EmptyState>
        </div>

        <!-- Nenhum resultado -->
        <div class="upload_no_results_section" v-else>
            <EmptyState
                icon="search"
                title="Nenhum resultado encontrado"
                description="Tente ajustar os filtros de busca ou usar termos diferentes"
            >
                <template #actions>
                    <sw-button
                        name="Criar Nova Página"
                        color="blue"
                        @click="addNewUpload"
                    />
                </template>
            </EmptyState>
        </div>

        <!-- Modal Em Desenvolvimento -->
        <sw-modal 
            id="modal_developmentUploadModal"
            :visible="showDevelopmentModal" 
            @update="closeDevelopmentModal"
        >
            <template #header>
                <ModalHeader
                    icon="code"
                    title="Funcionalidade em Desenvolvimento"
                    subtitle="Esta feature está sendo implementada"
                    color="orange"
                />
            </template>
            
            <template #content>
                <div class="modalContent">
                    <div class="developmentContent">
                        <div class="developmentContent_icon">
                            <sw-icon name="tools" />
                        </div>
                        <div class="developmentContent_info">
                            <p>Esta funcionalidade está em desenvolvimento e estará disponível em breve.</p>
                            <div class="developmentContent_features modal-section">
                                <h4>Recursos que estarão disponíveis:</h4>
                                <ul>
                                    <li>✨ Criação de links de upload personalizados</li>
                                    <li>📋 Tipos de documentos aceitos configuráveis</li>
                                    <li>📏 Limite de tamanho de arquivo ajustável</li>
                                    <li>🔒 Upload com ou sem autenticação</li>
                                    <li>📧 Notificações automáticas por email</li>
                                    <li>📊 Relatórios de uploads recebidos</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <div class="modalActions">
                        <sw-button name="Entendi" color="blue" @click="closeDevelopmentModal" />
                    </div>
                </div>
            </template>
        </sw-modal>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';

    import { fetchUploadLinks } from '@/services/mockApi';
    import { BOX_VARIANTS } from '@/constants';

    import Box from '@Component/boxs/BoxComponent';
    import UploadLinkCard from '@Component/webPages/UploadLinkCard';
    import WebLinkEmptyState from '@Component/webPages/WebLinkEmptyState';
    import ModalHeader from '@Component/modals/ModalHeader';
    import FilterSection from '@Component/filters/FilterSection';
    import EmptyState from '@Component/empty-states/EmptyState';

    const loadingUploads = ref(false);
    const uploads = ref([]);
    const error = ref(null);
    const showDevelopmentModal = ref(false);
    const searchQuery = ref('');

    // Ações do header
    const headerActions = computed(() => [
        {
            type: 'button',
            icon: 'plus',
            text: 'Nova Página',
            title: 'Criar nova página de upload',
            key: 'create'
        }
    ]);

    // Configuração dos filtros para o FilterSection
    const filterOptions = ref([
        {
            key: 'status',
            label: 'Status',
            options: [
                { value: '', label: 'Todos os Status' },
                { value: 'active', label: 'Ativo' },
                { value: 'inactive', label: 'Inativo' }
            ]
        },
        {
            key: 'type',
            label: 'Tipo',
            options: [
                { value: '', label: 'Todos os Tipos' },
                { value: 'public', label: 'Público' },
                { value: 'private', label: 'Privado' },
                { value: 'auth', label: 'Com Autenticação' }
            ]
        }
    ]);

    const filterValues = ref({
        status: '',
        type: ''
    });

    // Computed para estatísticas
    const totalUploads = computed(() => uploads.value.length);

    const activeUploadsCount = computed(() => {
        return uploads.value.filter(upload => upload.active).length;
    });

    const totalReceived = computed(() => {
        return uploads.value.reduce((total, upload) => total + (upload.received || 0), 0);
    });

    const totalPending = computed(() => {
        return uploads.value.reduce((total, upload) => total + (upload.pending || 0), 0);
    });


    // Computed para filtros
    const filteredUploads = computed(() => {
        let filtered = uploads.value;

        if (searchQuery.value) {
            filtered = filtered.filter(upload =>
                upload.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                upload.description?.toLowerCase().includes(searchQuery.value.toLowerCase())
            );
        }

        if (filterValues.value.status) {
            filtered = filtered.filter(upload => {
                if (filterValues.value.status === 'active') return upload.active;
                if (filterValues.value.status === 'inactive') return !upload.active;
                return true;
            });
        }

        if (filterValues.value.type) {
            filtered = filtered.filter(upload => upload.type === filterValues.value.type);
        }

        return filtered;
    });

    const loadUploadLinks = async () => {
        try {
            loadingUploads.value = true;
            error.value = null;

            const response = await fetchUploadLinks();

            if (response.status === 'success') {
                uploads.value = response.data;
            } else {
                error.value = response.error.message;
                console.error('Erro ao carregar links de upload:', response.error);
            }
        } catch (err) {
            error.value = 'Erro de conexão. Tente novamente.';
            console.error('Erro na requisição de links de upload:', err);
        } finally {
            loadingUploads.value = false;
        }
    };

    onMounted(() => {
        loadUploadLinks();
    });

    // Métodos para filtros
    const updateFilter = ({ key, value }) => {
        filterValues.value[key] = value;
    };

    const clearFilters = () => {
        searchQuery.value = '';
        filterValues.value = {
            status: '',
            type: ''
        };
    };

    const handleAction = (action) => {
        console.log('Action clicada:', action);
        if (action.click === 'addNewUpload') {
            addNewUpload();
        }
    };

    const handleHeaderAction = (action) => {
        console.log('Action received:', action);
        if (action.key === 'create') {
            addNewUpload();
        }
    };

    const addNewUpload = () => {
        console.log('Adicionar novo link de upload - Em desenvolvimento');
        showDevelopmentModal.value = true;
    };

    const closeDevelopmentModal = () => {
        showDevelopmentModal.value = false;
    };

    const handleCopyLink = (copyData) => {
        console.log('Link copiado:', copyData);
        // Aqui você pode adicionar lógica adicional se necessário
    };

    const viewUpload = (upload) => {
        console.log('Visualizar link de upload:', upload);
        // Simula redirecionamento para localhost com base nos dados do upload
        const companyName = getCompanyNameFromUpload(upload);
        const urlCustom = getUrlCustomFromUpload(upload);
        let localUrl = `/upload/${companyName}/${urlCustom}`;

        // Adiciona parâmetro protected=true se o item requer autenticação
        if (upload.settings?.requireAuth) {
            localUrl += '?protected=true';
        }

        console.log('Redirecionando para:', localUrl);
        window.open(localUrl, '_blank');
    };

    // Função para extrair nome da empresa do upload
    const getCompanyNameFromUpload = (upload) => {
        // Extrai o nome da empresa do título ou cria um baseado no ID
        if (upload.title.includes('TechSolutions')) {
            return 'techsolutions';
        } else if (upload.title.includes('DataCorp')) {
            return 'datacorp';
        } else if (upload.title.includes('StartupXYZ')) {
            return 'startupxyz';
        } else if (upload.title.includes('Consultoria')) {
            return 'consultoria-abc';
        } else {
            return `company-${upload.id}`;
        }
    };

    // Função para extrair URL customizada do upload
    const getUrlCustomFromUpload = (upload) => {
        // Cria uma URL amigável baseada no título
        if (upload.title.includes('Documentos RH')) {
            return 'documentos-rh';
        } else if (upload.title.includes('Propostas Comerciais')) {
            return 'propostas-comerciais';
        } else if (upload.title.includes('Curriculos')) {
            return 'curriculos-2024';
        } else if (upload.title.includes('Contratos')) {
            return 'contratos-servicos';
        } else if (upload.title.includes('Certificados')) {
            return 'certificados-qualificacao';
        } else {
            return `upload-${upload.id}`;
        }
    };

    const editUpload = (upload) => {
        console.log('Editar link de upload:', upload);
        showDevelopmentModal.value = true;
    };

    const deleteUpload = (upload) => {
        console.log('Excluir link de upload:', upload);
        if (confirm(`Tem certeza que deseja excluir o link de upload "${upload.title}"?`)) {
            showDevelopmentModal.value = true;
        }
    };

</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .uploadLinks {}

// Seção de estatísticas de upload
.upload_stats_section {
    margin-bottom: 2em;

    .row {
        align-items: stretch;
    }
}

.upload_stat_card {
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
    border-radius: 1.25em;
    padding: 1.5em;
    border: 1px solid rgba(226, 232, 240, 0.8);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, transparent, var(--linkColor), transparent);
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover {
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.08),
            0 1px 2px rgba(0, 0, 0, 0.05);
        border-color: rgba(59, 130, 246, 0.2);

        &::before {
            opacity: 1;
        }
    }
}

.upload_stat_header {
    display: flex;
    align-items: flex-start;
    gap: 1.25em;
    margin-bottom: 1.25em;
    flex: 1;
}

.upload_stat_icon {
    width: 3.25em;
    height: 3.25em;
    border-radius: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125em;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    position: relative;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05));
        pointer-events: none;
    }
}

.upload_stat_info {
    flex: 1;
}

.upload_stat_label {
    font-size: 0.875em;
    color: rgba(100, 116, 139, 0.8);
    font-weight: 600;
    margin-bottom: 0.5em;
    display: block;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.upload_stat_numbers {
    display: flex;
    align-items: baseline;
    gap: 0.5em;
    margin-bottom: 0.25em;
}

.upload_stat_main {
    font-size: 2.25em;
    font-weight: 800;
    color: var(--textPrimary);
    line-height: 1;
}

.upload_stat_secondary {
    font-size: 0.9em;
    color: rgba(100, 116, 139, 0.7);
    font-weight: 500;
}

// Cards compactos de estatísticas
.upload_stat_card--compact {
    padding: 1.25em;

    .upload_stat_header {
        margin-bottom: 1em;
    }

    .upload_stat_icon {
        width: 2.75em;
        height: 2.75em;
        font-size: 1em;
    }

    .upload_stat_label {
        font-size: 0.8em;
        margin-bottom: 0.375em;
    }

    .upload_stat_numbers {
        gap: 0.375em;
    }

    .upload_stat_main {
        font-size: 1.75em;
        font-weight: 700;
    }

    .upload_stat_secondary {
        font-size: 0.8em;
    }
}

// Variações de cores dos cards
.upload_stat_card--primary .upload_stat_icon {
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    color: white;
}

.upload_stat_card--success .upload_stat_icon {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
}


// Seção de conteúdo
.upload_content {
    background: var(--backgroundColorLighter);
    border-radius: 1.5em;
    padding: 25px 0px;
    border: 1px solid var(--borderColorLight);
}

// Estado de loading
.upload_loading_section {
    background: var(--backgroundColorLighter);
    border-radius: 1.5em;
    border: 1px solid var(--borderColorLight);
    text-align: center;
    padding: 4em 2em;
    display: flex;
    justify-content: center;
    align-items: center;
}

// Estados vazios (usando EmptyState component)
.upload_empty_section,
.upload_no_results_section {
    background: var(--backgroundColorLighter);
    border-radius: 1.5em;
    border: 1px solid var(--borderColorLight);
}

    /* Development Modal Styles */
    .developmentContent {
        display: flex;
        gap: 1.5em;
        align-items: flex-start;
        margin-bottom: 2em;
    }

    .developmentContent_icon {
        width: 4em;
        height: 4em;
        border-radius: 1em;
        background: linear-gradient(135deg, var(--colorOrange, #f59e0b), var(--colorOrangeDark, #d97706));
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.75em;
        box-shadow: 0 8px 20px rgba(245, 158, 11, 0.3);
        flex-shrink: 0;
    }

    .developmentContent_info {
        flex: 1;

        p {
            font-size: 1em;
            color: var(--textSecondary);
            margin-bottom: 1.5em;
            line-height: 1.6;
        }
    }

    .developmentContent_features {
        h4 {
            font-size: 1em;
            font-weight: 600;
            color: var(--textPrimary);
            margin-bottom: 1em;
        }

        ul {
            list-style: none;
            padding: 0;
            margin: 0;

            li {
                display: flex;
                align-items: center;
                gap: 0.75em;
                padding: 0.625em 0;
                font-size: 0.9375em;
                color: var(--textSecondary);
                border-bottom: 1px solid var(--borderColorLight, #e5e7eb);

                &:last-child {
                    border-bottom: none;
                }

                &:before {
                    content: '';
                    width: 0.5em;
                    height: 0.5em;
                    border-radius: 50%;
                    background: var(--colorBlue, #3b82f6);
                    flex-shrink: 0;
                }
            }
        }
    }

    /* Modal styles override */
    #modal_developmentUploadModal {
        .sw_modal_container {
            max-width: 600px !important;
        }
    }

    @include screen_medias(s, m) {
        .developmentContent {
            flex-direction: column;
            text-align: center;
        }

        .developmentContent_icon {
            align-self: center;
        }
    }
</style>